/**
 * VoiceProviderToggle Component
 * 
 * A compact toggle switch for switching between Deepgram and default voice providers.
 * Can be placed in settings or inline in recording UI.
 * Includes voice selection modal for choosing specific TTS voice.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Typography,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Mic as MicIcon,
  VolumeUp as VolumeUpIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Woman as WomanIcon,
  Man as ManIcon,
  PlayArrow as PlayIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAuth } from '@healthdesk/shared-hooks';
import {
  getVoicePreferences,
  setSTTProvider,
  setTranslationSTTProvider,
  setTTSProvider,
  normalizeSttProvider,
  saveVoicePreferences,
  GEMINI_LIVE_DEFAULT_VOICE,
  GEMINI_LIVE_PREBUILT_VOICES,
  isChartMindGeminiLiveSttOptionEnabled,
} from '../../services/voice';
import { deepgramTTSService, AURA_VOICES } from '../../services/voice/deepgramTTSService';
import { setStoredVoiceBackend, SC3_VOICE_BACKEND_STORAGE_KEY } from './voiceConversationFlags';

// OpenAI TTS voices
const OPENAI_VOICES = {
  alloy: { gender: 'neutral', description: 'Neutral, balanced voice' },
  echo: { gender: 'male', description: 'Warm male voice' },
  fable: { gender: 'male', description: 'British accent, storyteller style' },
  onyx: { gender: 'male', description: 'Deep, authoritative male voice' },
  nova: { gender: 'female', description: 'Warm, friendly female voice' },
  shimmer: { gender: 'female', description: 'Clear, expressive female voice' },
};

/** @param {string|undefined} raw */
function normalizeTtsProviderSelection(raw) {
  if (raw === 'deepgram' || raw === 'gemini_flash_tts') return raw;
  return 'openai';
}

/** Primary-selected ToggleButton: white label on blue (matches theme primary). */
export const voiceToggleButtonSx = {
  '&.Mui-selected': {
    color: 'primary.contrastText',
    backgroundColor: 'primary.main',
    '&:hover': {
      backgroundColor: 'primary.dark',
      color: 'primary.contrastText',
    },
  },
};

/**
 * Compact toggle for STT provider
 */
export const STTProviderToggle = ({ 
  onChange, 
  disabled = false,
  showLabel = true,
  size = 'medium',
}) => {
  const { user } = useAuth();
  const [sttMode, setSttMode] = useState('web');
  const [loading, setLoading] = useState(true);
  const geminiOpt = isChartMindGeminiLiveSttOptionEnabled();

  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      
      try {
        const prefs = await getVoicePreferences(user.uid);
        setSttMode(normalizeSttProvider(prefs.sttProvider));
      } catch (error) {
        console.error('[STTProviderToggle] Error loading preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user?.uid]);

  const handleSttGroup = async (_e, value) => {
    if (value == null || !user?.uid) return;
    setSttMode(value);
    try {
      await setSTTProvider(user.uid, value);
      onChange?.(value);
    } catch (error) {
      console.error('[STTProviderToggle] Error saving preference:', error);
    }
  };

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'stretch' }}>
      {showLabel && (
        <Tooltip title="ChartMind encounter (single language): Web Speech, Deepgram, or Gemini Live (beta) scribe">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MicIcon fontSize={size} color="action" />
            <Typography variant={size === 'small' ? 'caption' : 'body2'}>Speech-to-text</Typography>
          </Box>
        </Tooltip>
      )}
      <ToggleButtonGroup
        exclusive
        value={sttMode}
        onChange={handleSttGroup}
        disabled={disabled || !user?.uid}
        size={size === 'small' ? 'small' : 'medium'}
        fullWidth
        color="primary"
      >
        <ToggleButton value="web" sx={voiceToggleButtonSx}>
          Web Speech
        </ToggleButton>
        <ToggleButton value="deepgram" sx={voiceToggleButtonSx}>
          Deepgram
        </ToggleButton>
        <ToggleButton value="gemini_live" disabled={!geminiOpt} sx={voiceToggleButtonSx}>
          Gemini Live (beta)
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

// NOTE: TTSProviderToggle is exported but not currently mounted in the
// app. VoiceSettingsPanel.jsx owns the primary TTS provider RadioGroup.

/**
 * Compact control for TTS provider (OpenAI, Deepgram Aura, or Gemini 3.1 Flash TTS preview)
 */
export const TTSProviderToggle = ({ 
  onChange, 
  disabled = false,
  showLabel = true,
  size = 'medium',
}) => {
  const { user } = useAuth();
  const [ttsProvider, setTtsProviderLocal] = useState('openai');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      
      try {
        const prefs = await getVoicePreferences(user.uid);
        setTtsProviderLocal(normalizeTtsProviderSelection(prefs.ttsProvider));
      } catch (error) {
        console.error('[TTSProviderToggle] Error loading preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user?.uid]);

  const handleTtsSelect = async (event) => {
    const newValue = event.target.value;
    
    if (!user?.uid) {
      console.warn('[TTSProviderToggle] No user ID');
      return;
    }

    const previous = ttsProvider;
    setTtsProviderLocal(newValue);
    
    try {
      await setTTSProvider(user.uid, newValue);
      onChange?.(newValue);
    } catch (error) {
      console.error('[TTSProviderToggle] Error saving preference:', error);
      setTtsProviderLocal(previous);
    }
  };

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showLabel && (
        <Tooltip title="Text-to-Speech provider">
          <VolumeUpIcon fontSize={size} color="action" />
        </Tooltip>
      )}
      <FormControl size="small" disabled={disabled || !user?.uid} sx={{ minWidth: 200 }}>
        <Select
          value={ttsProvider}
          onChange={(e) => void handleTtsSelect(e)}
          size="small"
        >
          <MenuItem value="openai">OpenAI</MenuItem>
          <MenuItem value="deepgram">Deepgram Aura</MenuItem>
          <MenuItem value="gemini_flash_tts">Gemini 3.1 Flash TTS ✦ Preview</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

/**
 * Voice Selection Modal
 * Shows available voices based on current TTS provider (OpenAI or Deepgram)
 */
const VoiceSelectionModal = ({
  open,
  onClose,
  ttsProvider,
  currentVoice,
  onVoiceSelect,
  showGeminiLiveSection = false,
  currentGeminiLiveVoice,
  onGeminiLiveVoiceSelect,
  showSc3VoiceBackendSection = false,
  sc3VoiceBackendDraft = 'legacy',
  onSc3VoiceBackendDraftChange,
  onSc3VoiceBackendPersist,
}) => {
  const [selectedVoice, setSelectedVoice] = useState(currentVoice);
  const [selectedGeminiLiveVoice, setSelectedGeminiLiveVoice] = useState(
    currentGeminiLiveVoice || GEMINI_LIVE_DEFAULT_VOICE
  );
  const [previewPlaying, setPreviewPlaying] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelectedVoice(currentVoice);
    setSelectedGeminiLiveVoice(currentGeminiLiveVoice || GEMINI_LIVE_DEFAULT_VOICE);
  }, [open, currentVoice, currentGeminiLiveVoice]);

  const voices = ttsProvider === 'deepgram' ? AURA_VOICES : OPENAI_VOICES;
  const voiceEntries = Object.entries(voices);
  const geminiEntries = Object.entries(GEMINI_LIVE_PREBUILT_VOICES);

  // Group voices by gender
  const femaleVoices = voiceEntries.filter(([, info]) => info.gender === 'female');
  const maleVoices = voiceEntries.filter(([, info]) => info.gender === 'male');
  const neutralVoices = voiceEntries.filter(([, info]) => info.gender === 'neutral');

  const handleSelect = (voiceId) => {
    setSelectedVoice(voiceId);
  };

  const handleSave = async () => {
    await onVoiceSelect(selectedVoice);
    if (showGeminiLiveSection && onGeminiLiveVoiceSelect) {
      await onGeminiLiveVoiceSelect(selectedGeminiLiveVoice);
    }
    if (showSc3VoiceBackendSection && onSc3VoiceBackendPersist) {
      await onSc3VoiceBackendPersist(sc3VoiceBackendDraft);
    }
    onClose();
  };

  const disableClassicVoicePreview =
    showSc3VoiceBackendSection && sc3VoiceBackendDraft !== 'legacy';

  const handlePreview = async (voiceId) => {
    if (previewPlaying) return;
    
    setPreviewPlaying(voiceId);
    try {
      if (ttsProvider === 'deepgram') {
        await deepgramTTSService.speak('Hello, this is a preview of my voice.', { voice: voiceId });
      } else {
        // For OpenAI, we'd need to call the TTS function - skip preview for now
      }
    } catch (error) {
      console.error('[VoiceSelection] Preview error:', error);
    } finally {
      setPreviewPlaying(null);
    }
  };

  const getGenderIcon = (gender) => {
    if (gender === 'female') return <WomanIcon />;
    if (gender === 'male') return <ManIcon />;
    return <PersonIcon />;
  };

  const innerAccordionSx = {
    boxShadow: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
    '&:not(:last-child)': { mb: 0.75 },
  };

  const outerAccordionSx = {
    ...innerAccordionSx,
    mb: 1,
    '&:last-child': { mb: 0 },
  };

  const renderVoiceRows = (voiceList, { selection, onSelect, showDeepgramPreview = false } = {}) => {
    if (voiceList.length === 0) return null;
    const sel = selection ?? selectedVoice;
    const pick = onSelect ?? handleSelect;

    return (
      <List dense disablePadding>
        {voiceList.map(([voiceId, info]) => (
          <ListItem key={voiceId} disablePadding>
            <ListItemButton
              selected={sel === voiceId}
              onClick={() => pick(voiceId)}
              sx={{
                px: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(27, 69, 132, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(27, 69, 132, 0.12)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Radio
                  checked={sel === voiceId}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': { color: 'primary.main' },
                  }}
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {getGenderIcon(info.gender)}
              </ListItemIcon>
              <ListItemText
                primary={voiceId.charAt(0).toUpperCase() + voiceId.slice(1)}
                secondary={info.description || info.accent}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              {showDeepgramPreview && ttsProvider === 'deepgram' && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(voiceId);
                  }}
                  disabled={previewPlaying !== null}
                  sx={{ ml: 1 }}
                >
                  {previewPlaying === voiceId ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlayIcon fontSize="small" />
                  )}
                </IconButton>
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderGenderAccordion = (title, voiceList, rowOptions) => {
    if (!voiceList.length) return null;
    return (
      <Accordion disableGutters elevation={0} sx={innerAccordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
          sx={{
            minHeight: 40,
            px: 1.5,
            '& .MuiAccordionSummary-content': { my: 1 },
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pb: 1 }}>{renderVoiceRows(voiceList, rowOptions)}</AccordionDetails>
      </Accordion>
    );
  };

  const classicGenderAccordions = (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
      {renderGenderAccordion('Female voices', femaleVoices, {
        showDeepgramPreview: true && !disableClassicVoicePreview,
      })}
      {renderGenderAccordion('Male voices', maleVoices, {
        showDeepgramPreview: true && !disableClassicVoicePreview,
      })}
      {renderGenderAccordion('Neutral voices', neutralVoices, {
        showDeepgramPreview: true && !disableClassicVoicePreview,
      })}
    </Box>
  );

  const geminiFemale = geminiEntries.filter(([, info]) => info.gender === 'female');
  const geminiMale = geminiEntries.filter(([, info]) => info.gender === 'male');
  const geminiNeutral = geminiEntries.filter(([, info]) => info.gender === 'neutral');

  const geminiGenderAccordions = (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
      {renderGenderAccordion('Female voices', geminiFemale, {
        selection: selectedGeminiLiveVoice,
        onSelect: setSelectedGeminiLiveVoice,
        showDeepgramPreview: false,
      })}
      {renderGenderAccordion('Male voices', geminiMale, {
        selection: selectedGeminiLiveVoice,
        onSelect: setSelectedGeminiLiveVoice,
        showDeepgramPreview: false,
      })}
      {renderGenderAccordion('Neutral voices', geminiNeutral, {
        selection: selectedGeminiLiveVoice,
        onSelect: setSelectedGeminiLiveVoice,
        showDeepgramPreview: false,
      })}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VolumeUpIcon sx={{ color: 'primary.main' }} />
          {showGeminiLiveSection ? 'Voice settings' : 'Select TTS Voice'}
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {showGeminiLiveSection
            ? 'Voice backend for Symptom Checker 3, then Classic TTS and Gemini Live (beta) voices.'
            : ttsProvider === 'deepgram'
              ? 'Deepgram Aura Voices'
              : 'OpenAI TTS Voices'}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 1.5, maxHeight: showGeminiLiveSection ? 620 : 440 }}>
        {showSc3VoiceBackendSection && onSc3VoiceBackendDraftChange ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
              Symptom Checker 3 — voice backend
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Only one audio pipeline runs at a time. Classic uses OpenAI / Deepgram below. Gemini Live and OpenAI
              Realtime use native model audio (no classic TTS). Changing backend applies after you tap Save and may
              restart the session.
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={sc3VoiceBackendDraft}
              onChange={(_e, value) => {
                if (value == null) return;
                onSc3VoiceBackendDraftChange(value);
              }}
              size="small"
              fullWidth
              color="primary"
            >
              <ToggleButton value="legacy" sx={voiceToggleButtonSx}>
                Classic pipeline
              </ToggleButton>
              <ToggleButton value="gemini_live" sx={voiceToggleButtonSx}>
                Gemini Live (beta)
              </ToggleButton>
              <ToggleButton value="openai_realtime" sx={voiceToggleButtonSx}>
                OpenAI Realtime
              </ToggleButton>
            </ToggleButtonGroup>
            {disableClassicVoicePreview ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Deepgram voice previews are off while a native-audio backend is selected, so you do not hear two voices
                at once.
              </Typography>
            ) : null}
            <Divider sx={{ my: 1.5 }} />
          </Box>
        ) : null}
        {showGeminiLiveSection ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Accordion disableGutters elevation={0} defaultExpanded sx={outerAccordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { my: 1.25, flexDirection: 'column', alignItems: 'flex-start' },
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  Classic TTS (STT + TTS pipeline)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  OpenAI or Deepgram — used with the classic voice pipeline
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1, pb: 1.5, pt: 0 }}>{classicGenderAccordions}</AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={outerAccordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { my: 1.25, flexDirection: 'column', alignItems: 'flex-start' },
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  Gemini Live (beta)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Native audio voices. Used when the conversation uses the Gemini Live (beta) backend. Restart or start a
                  new session to apply.
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1, pb: 1.5, pt: 0 }}>{geminiGenderAccordions}</AccordionDetails>
            </Accordion>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>{classicGenderAccordions}</Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => void handleSave()} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Combined voice provider toggle with both STT and TTS
 */
const VoiceProviderToggle = ({
  onSTTChange,
  /** Called when translation STT changes; defaults to onSTTChange (e.g. refresh both prefs). */
  onTranslationSttChange,
  onTTSChange,
  onVoiceChange,
  /** When true (e.g. symptom checker), voice settings modal also configures Gemini Live prebuilt voice. */
  enableGeminiLiveVoiceSection = false,
  onGeminiLiveVoiceChange,
  /** When true (SC3), settings modal includes Symptom Checker 3 voice backend (Classic / Gemini Live / OpenAI Realtime). */
  enableSc3VoiceBackendInSettings = false,
  /** Current SC3 backend from parent (controlled). */
  sc3VoiceBackend = 'legacy',
  /** After Save in modal: persist + parent should remount conversation (e.g. bump key). */
  onSc3VoiceBackendChange,
  disabled = false,
  compact = false,
  showSettingsButton = false,
  onSettingsClick,
  hideSTTToggle = false, // Hide STT toggle when translation mode auto-selects STT provider
  hideCompactProviderChip = false,
}) => {
  const { user } = useAuth();
  const [sttMode, setSttMode] = useState('web');
  const [translationSttMode, setTranslationSttMode] = useState('web');
  const [ttsMode, setTtsMode] = useState('openai');
  const [loading, setLoading] = useState(true);
  const geminiSttOptionEnabled = isChartMindGeminiLiveSttOptionEnabled();
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedGeminiLiveVoice, setSelectedGeminiLiveVoice] = useState(GEMINI_LIVE_DEFAULT_VOICE);
  const [preferences, setPreferences] = useState(null);
  const [modalSc3Backend, setModalSc3Backend] = useState(sc3VoiceBackend);

  useEffect(() => {
    setModalSc3Backend(sc3VoiceBackend);
  }, [sc3VoiceBackend]);

  useEffect(() => {
    if (!voiceModalOpen || !enableSc3VoiceBackendInSettings) return;
    setModalSc3Backend(sc3VoiceBackend);
  }, [voiceModalOpen, enableSc3VoiceBackendInSettings, sc3VoiceBackend]);

  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      
      try {
        const prefs = await getVoicePreferences(user.uid);
        setPreferences(prefs);
        setSttMode(normalizeSttProvider(prefs.sttProvider));
        setTranslationSttMode(normalizeSttProvider(prefs.translationSttProvider));
        const tts = normalizeTtsProviderSelection(prefs.ttsProvider);
        setTtsMode(tts);
        // Load selected voice from preferences
        const currentProvider = tts === 'deepgram' ? 'deepgram' : 'openai';
        if (currentProvider === 'deepgram') {
          setSelectedVoice(prefs?.deepgram?.voice || 'asteria');
        } else {
          setSelectedVoice(prefs?.openai?.voice || 'nova');
        }
        setSelectedGeminiLiveVoice(prefs?.geminiLive?.voice || GEMINI_LIVE_DEFAULT_VOICE);
      } catch (error) {
        console.error('[VoiceProviderToggle] Error loading preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user?.uid]);

  const handleSttSegment = async (_event, value) => {
    if (value == null || !user?.uid) return;
    setSttMode(value);
    try {
      await setSTTProvider(user.uid, value);
      onSTTChange?.(value);
    } catch (error) {
      console.error('[VoiceProviderToggle] Error saving STT preference:', error);
    }
  };

  const notifyTranslationStt = onTranslationSttChange ?? onSTTChange;

  const handleTranslationSttSegment = async (_event, value) => {
    if (value == null || !user?.uid) return;
    setTranslationSttMode(value);
    try {
      await setTranslationSTTProvider(user.uid, value);
      notifyTranslationStt?.();
    } catch (error) {
      console.error('[VoiceProviderToggle] Error saving translation STT preference:', error);
    }
  };

  const handleTtsSegment = async (_event, value) => {
    if (value == null || !user?.uid) return;
    const prevTts = ttsMode;
    setTtsMode(value);
    if (value === 'deepgram') {
      setSelectedVoice(preferences?.deepgram?.voice || 'asteria');
    } else {
      setSelectedVoice(preferences?.openai?.voice || 'nova');
    }
    try {
      await setTTSProvider(user.uid, value);
      onTTSChange?.(value);
    } catch (error) {
      console.error('[VoiceProviderToggle] Error saving TTS preference:', error);
      setTtsMode(prevTts);
    }
  };

  const handleVoiceSelect = async (voiceId) => {
    if (!user?.uid) return;
    
    setSelectedVoice(voiceId);
    
    try {
      const currentProvider = ttsMode === 'deepgram' ? 'deepgram' : 'openai';
      const updatedPrefs = {
        ...preferences,
        [currentProvider]: {
          ...(preferences?.[currentProvider] || {}),
          voice: voiceId,
        },
      };
      
      await saveVoicePreferences(user.uid, updatedPrefs);
      setPreferences(updatedPrefs);
      
      // If Deepgram, configure the service with the new voice
      if (currentProvider === 'deepgram') {
        deepgramTTSService.configure({ voice: voiceId });
      }
      onVoiceChange?.(currentProvider, voiceId);
    } catch (error) {
      console.error('[VoiceProviderToggle] Error saving voice preference:', error);
    }
  };

  const handleGeminiLiveVoiceSave = async (voiceId) => {
    if (!user?.uid) return;
    try {
      const updatedPrefs = {
        ...preferences,
        geminiLive: { ...(preferences?.geminiLive || {}), voice: voiceId },
      };
      await saveVoicePreferences(user.uid, updatedPrefs);
      setPreferences(updatedPrefs);
      setSelectedGeminiLiveVoice(voiceId);
      onGeminiLiveVoiceChange?.(voiceId);
    } catch (error) {
      console.error('[VoiceProviderToggle] Error saving Gemini Live voice:', error);
    }
  };

  const handleOpenVoiceModal = () => {
    setVoiceModalOpen(true);
  };

  const handleSc3VoiceBackendPersist = async (backend) => {
    if (backend !== 'legacy' && backend !== 'gemini_live' && backend !== 'openai_realtime') return;
    setStoredVoiceBackend(backend, SC3_VOICE_BACKEND_STORAGE_KEY);
    if (backend !== sc3VoiceBackend) {
      onSc3VoiceBackendChange?.(backend);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (compact) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showSettingsButton && (
            <Tooltip title="Voice Settings">
              <IconButton
                size="small"
                onClick={handleOpenVoiceModal}
                disabled={disabled}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'rgba(27, 69, 132, 0.08)' },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!hideCompactProviderChip ? (
            <Tooltip title="Voice Provider">
              <Chip
                icon={<VolumeUpIcon />}
                label={
                  ttsMode === 'deepgram'
                    ? 'Deepgram'
                    : ttsMode === 'gemini_flash_tts'
                      ? 'Gemini TTS'
                      : 'OpenAI'
                }
                size="small"
                color={ttsMode !== 'openai' ? 'primary' : 'default'}
                variant={ttsMode !== 'openai' ? 'filled' : 'outlined'}
                onClick={handleOpenVoiceModal}
              />
            </Tooltip>
          ) : null}
        </Box>
        {/* Voice Selection Modal */}
        <VoiceSelectionModal
          open={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
          ttsProvider={ttsMode === 'deepgram' ? 'deepgram' : 'openai'}
          currentVoice={selectedVoice}
          onVoiceSelect={handleVoiceSelect}
          showGeminiLiveSection={enableGeminiLiveVoiceSection}
          currentGeminiLiveVoice={selectedGeminiLiveVoice}
          onGeminiLiveVoiceSelect={handleGeminiLiveVoiceSave}
          showSc3VoiceBackendSection={enableSc3VoiceBackendInSettings}
          sc3VoiceBackendDraft={modalSc3Backend}
          onSc3VoiceBackendDraftChange={setModalSc3Backend}
          onSc3VoiceBackendPersist={handleSc3VoiceBackendPersist}
        />
      </>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 1,
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Voice Provider Settings
          <Tooltip title="Encounter vs translation STT are separate for ChartMind testing. TTS is for read-aloud. Deepgram can cover listen, speak, or both.">
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Typography>
        <Tooltip title="Select TTS Voice">
          <IconButton 
            size="small" 
            onClick={handleOpenVoiceModal}
            disabled={disabled}
            sx={{ 
              color: 'primary.main',
              '&:hover': { backgroundColor: 'rgba(27, 69, 132, 0.08)' },
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* STT Toggle - hidden when translation mode auto-selects provider */}
        {!hideSTTToggle && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Encounter (listen)
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={sttMode}
                onChange={handleSttSegment}
                disabled={disabled || !user?.uid}
                size="small"
                fullWidth
                color="primary"
              >
                <ToggleButton value="web" sx={voiceToggleButtonSx}>
                  Web Speech
                </ToggleButton>
                <ToggleButton value="deepgram" sx={voiceToggleButtonSx}>
                  Deepgram
                </ToggleButton>
                <ToggleButton value="gemini_live" disabled={!geminiSttOptionEnabled} sx={voiceToggleButtonSx}>
                  Gemini Live (beta)
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                Single-language ChartMind recording (scribe). Mic → text; Deepgram = listen only here.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Translation services
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={translationSttMode}
                onChange={handleTranslationSttSegment}
                disabled={disabled || !user?.uid}
                size="small"
                fullWidth
                color="primary"
              >
                <ToggleButton value="web" sx={voiceToggleButtonSx}>
                  Web Speech
                </ToggleButton>
                <ToggleButton value="deepgram" sx={voiceToggleButtonSx}>
                  Deepgram
                </ToggleButton>
                <ToggleButton value="gemini_live" disabled={!geminiSttOptionEnabled} sx={voiceToggleButtonSx}>
                  Gemini Live (beta)
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                When provider and patient languages differ: classic STT + REST translation vs Live interpreter.
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Read aloud
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={ttsMode}
            onChange={handleTtsSegment}
            disabled={disabled || !user?.uid}
            size="small"
            fullWidth
            color="primary"
          >
            <ToggleButton value="openai" sx={voiceToggleButtonSx}>
              OpenAI
            </ToggleButton>
            <ToggleButton value="deepgram" sx={voiceToggleButtonSx}>
              Deepgram
            </ToggleButton>
            <ToggleButton
              value="gemini_flash_tts"
              title="Gemini 3.1 Flash TTS (Preview)"
              sx={voiceToggleButtonSx}
            >
              Gemini TTS
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            Translation playback. Deepgram here is only for speaking.
          </Typography>
        </Box>

        {/* Selected Voice Display */}
        {selectedVoice && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mt: 0.5,
          }}>
            <Typography variant="caption" color="text.secondary">
              Voice: 
            </Typography>
            <Chip
              label={selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)}
              size="small"
              variant="outlined"
              onClick={handleOpenVoiceModal}
              disabled={disabled}
              sx={{ 
                cursor: 'pointer',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(27, 69, 132, 0.08)' },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Voice Selection Modal */}
      <VoiceSelectionModal
        open={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
        ttsProvider={ttsMode === 'deepgram' ? 'deepgram' : 'openai'}
        currentVoice={selectedVoice}
        onVoiceSelect={handleVoiceSelect}
        showGeminiLiveSection={enableGeminiLiveVoiceSection}
        currentGeminiLiveVoice={selectedGeminiLiveVoice}
        onGeminiLiveVoiceSelect={handleGeminiLiveVoiceSave}
        showSc3VoiceBackendSection={enableSc3VoiceBackendInSettings}
        sc3VoiceBackendDraft={modalSc3Backend}
        onSc3VoiceBackendDraftChange={setModalSc3Backend}
        onSc3VoiceBackendPersist={handleSc3VoiceBackendPersist}
      />
    </Box>
  );
};

export default VoiceProviderToggle;
